<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { getErrorCode } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import {
  DBtn,
  DTextField,
  DTable,
  DLoading,
  DModal,
  XIcon,
  CheckIcon,
  Loading02Icon
} from 'front-library'
import {
  getReversePayrollRequest,
  ListReversePayroll
} from '@/app/core/services/reverse-payrolls'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)

/* Datos */
const search = ref()
const tableReverse = ref(null)

const refresh = ref()
const isRefreshData = ref<boolean>(false)

/* Datos componente table */
const columns = ref([
  {
    name: 'processName',
    title: t('reversePayrolls.list.process'),
    table: 'processes.name'
  },
  {
    name: 'status_process',
    title: t('reversePayrolls.list.status'),
    table: 'process_status.name'
  },
  {
    name: 'observationText',
    title: t('reversePayrolls.list.reason'),
    table: 'reverse_payroll_requests.observation'
  },
  { name: 'errors_list', title: t('reversePayrolls.list.errors') },
  {
    name: 'startDate',
    title: t('reversePayrolls.list.startDate'),
    table: 'start_date'
  },
  {
    name: 'endDate',
    title: t('reversePayrolls.list.endDate'),
    table: 'end_date'
  },
  {
    name: 'list_entries',
    title: t('reversePayrolls.list.ledgerTrasactions'),
    table: 'ledger_transactions'
  },
  { name: 'user', title: t('reversePayrolls.list.user'), table: 'user' },
  {
    name: 'createdAt',
    title: t('reversePayrolls.list.creationDate'),
    table: 'reverse_payroll_requests.created_at'
  }
])
const rows = ref<ListReversePayroll[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')
const listErrorsEntries = ref()
const showModalErrorsEntries = ref(false)

/* Redirect's */
const createReversePayroll = () => {
  void router.push({ name: 'CreateReversePayroll' })
}

const closeModal = () => {
  showModalErrorsEntries.value = false
}

/* Listar reversos de nóminas */
const reversePayrolls = async (load: boolean = true) => {
  if (load) loading.value = true
  isRefreshData.value = true
  try {
    const data: any = await getReversePayrollRequest(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
    isRefreshData.value = false
  } catch (error: any) {
    loading.value = false
    const code = getErrorCode(error.response.data?.code)
    notifications.addNotification({
      title: t('error.titles.' + code),
      text: t('error.messages.' + code),
      type: 'error',
      time: 10000
    })
  }
}

onMounted(() => {
  refresh.value = setInterval(() => {
    void reversePayrolls(false)
  }, 10000)
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModalErrorsEntries"
    :title="t('reversePayrolls.list.errorEntries')"
    @closeModal="closeModal">
    <template v-slot:description>
      <div class="text-left" style="max-height: calc(100vh - 15rem)">
        <div
          v-for="(error, index) in listErrorsEntries"
          :key="index"
          class="text-gray-600">
          <span class="text-blue-700">&#9205;</span> {{ error?.message || '' }}
        </div>
      </div>
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('reversePayrolls.list.title')" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('reversePayrolls.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="reversePayrolls" />
          <DBtn
            @click.prevent="reversePayrolls"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createReversePayroll()"
          v-if="store.hasPermission('reverse-request:create')">
          {{
            t('form.button.create', {
              form: t('reversePayrolls.list.reverse')
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableReverse"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), reversePayrolls()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), reversePayrolls()">
      <template #status_process="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.status === 'ERROR'
              ? 'bg-red-100 text-red-600'
              : data.status === 'COMPLETE'
              ? 'bg-green-100 text-green-600'
              : data.status === 'PENDING'
              ? 'bg-blue-100 text-blue-600'
              : 'bg-gray-100 text-gray-600'
          ">
          {{ t('status.' + data.status) }}
        </div>
      </template>
      <template #errors_list="data: any">
        <div class="flex items-center rounded-full w-fit text-sm py-1">
          <XIcon class="text-red-700" v-if="data.errors.length > 0"></XIcon>
          {{ data.errors.toString() }}
        </div>
      </template>

      <template #observationText="data: any">
        <div class="flex items-center rounded-full w-fit text-sm py-1">
          {{ data.observation }}
        </div>
      </template>

      <template #list_entries="data: any">
        <div v-if="
              Array.isArray(data.ledgerTransactions) &&
              data.ledgerTransactions.length > 0
            " class="flex items-center justify-between">
          <div>
            <p v-for="(error, index) in data.ledgerTransactions"
              :key="index" class="text-sm text-gray-500">
              <span class="text-blue-700">&#9205;</span>
              {{ data.ledgerTransactions[index] }}
            </p>
          </div>
          <div v-if="data.statusReverseAccounting === 'Error'" @click="
              ;(showModalErrorsEntries = data?.errorsReverseAccounting && data.errorsReverseAccounting.length > 0 ),
                (listErrorsEntries = data?.errorsReverseAccounting && data.errorsReverseAccounting.length > 0 
                  ? data.errorsReverseAccounting
                  : [t('processedPayrolls.list.noErrorEntries')])
            ">
            <XIcon class="text-red-500" />
          </div>
          <div v-if="data.statusReverseAccounting === 'Completado'">
            <CheckIcon class="text-green-500" />
          </div>
          <div
            class="flex flex-col items-center"
            v-if="data.statusReverseAccounting === 'Pendiente'">
            <Loading02Icon class="rotate text-blue-600" />
            <span class="font-black text-xs tracking-wider text-blue-500">
              {{ `Procesando...` }}</span
            >
          </div>
        </div>
      </template>
      <template #refresh v-if="isRefreshData">
        <Loading02Icon class="rotate text-gray-400 mr-2" />
        <h1
          class="text-gray-400 text-base"
          v-html="t('reversePayrolls.list.updateData')" />
      </template>
    </DTable>
  </div>
</template>
